
import Vue from 'vue';
import { ROUTER } from '@/router/constants';
import { mapGetters, mapState } from 'vuex';
import TeamMembersTableCell from '@/views/Team/CompanyDashboard/TeamMembersTableCell.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import { calcRunnitTokensDataForBalanceAccount, Team, CompanyCustomFieldDef } from '@run-diffusion/shared';
import { roundAllocationSettingsPercentToNearestStep } from '@/views/Team/TeamDashboard/SetTeamAllocationSettings/utils';
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import { TeamUserMixin } from '@/mixins';
import IsBoundToBudgetBadge from '@/views/Team/components/IsBoundToBudgetBadge.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';

export default Vue.extend({
	name: 'TeamRow',
	props: {
		currTeam: {
			type: Object as () => Team,
			required: true,
		},
		// Added on this page and shown ahead of the listing (utils/justAdded.ts)
		justAdded: { type: Boolean, default: false },
	},
	data () {
		return {
			MORE_ACTIONS_ITEM_VALUE: {
				EDIT: 'edit',
				TRANSFER_TOKENS: 'transfer-tokens',
				DEACTIVATE: 'deactivate',
				ACTIVATE: 'activate',
				DUP: 'dup',
			},
			loadingLocalBalanceAccount: false,
			localBalanceAccount: null,
		};
	},
	mixins: [
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
		]),
		teamCustomFieldDefs(): CompanyCustomFieldDef[] {
			return this.team?.company?.teamCustomFieldDefs || [];
		},
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		allocationPercentage (): number {
			const currentBudget: number = this.currTeam?.allotmentSettings?.monthlyRunnitTokensBudget > 0
				? this.currTeam.allotmentSettings.monthlyRunnitTokensBudget
				: 0;
			const totalBudget: number = this.team?.company?.sumMonthlyRunnitTokensBudget > 0
				? this.team.company.sumMonthlyRunnitTokensBudget
				: 1;

			// Calculate percentage allocation
			const currentTeamPercent: number = currentBudget / totalBudget * 100;
			return roundAllocationSettingsPercentToNearestStep(currentTeamPercent);
		},
		allocationPercentageStr (): string {
			return this.allocationPercentage ? `${this.allocationPercentage}%` : '';
		},
		moreActionsMenuItems () {
			let idIncrementer: number = 0;
			return [
				...(!this.$route.query.deactivated ? [
					...(this.hasManageTeamsPermission || this.hasManageCompanyPermission ? [
						{
							id: idIncrementer++,
							icon: 'mdi-pencil-outline',
							label: 'Edit',
							description: this.hasManageCompanyPermission ? `Update ${this.displayLabels.team} and budget settings` : `Update ${this.displayLabels.team} settings`,
							value: this.MORE_ACTIONS_ITEM_VALUE.EDIT,
						},
					] : []),
					...(this.companyClubOfferBenefits.isEnterprise && this.hasManageCompanyPermission ? [
						{
							id: idIncrementer++,
							icon: 'mdi-transfer',
							label: 'Transfer Tokens',
							description: `To/From Company Token Bank`,
							value: this.MORE_ACTIONS_ITEM_VALUE.TRANSFER_TOKENS,
						},
					] : []),
					...(this.companyClubOfferBenefits.isEnterprise && this.hasManageTeamsPermission ? [
						{
							id: idIncrementer++,
							icon: 'mdi-content-duplicate',
							label: `Duplicate`,
							description: `Create new ${this.displayLabels.team} from these settings`,
							value: this.MORE_ACTIONS_ITEM_VALUE.DUP,
						},
						{
							id: idIncrementer++,
							icon: 'mdi-account-multiple-remove-outline',
							iconStyle: { color: 'var(--red-500)' },
							descriptionStyle: { color: 'var(--grey-400)' },
							label: 'Deactivate',
							description: `Deactivate and disable ${this.displayLabels.team}`,
							value: this.MORE_ACTIONS_ITEM_VALUE.DEACTIVATE,
						},
					] : []),
				] : [
					...(this.companyClubOfferBenefits.isEnterprise && this.hasManageTeamsPermission ? [
						{
							id: idIncrementer++,
							icon: 'mdi-check-circle-outline',
							label: 'Reactivate',
							description: `Activate and enable ${this.displayLabels.team}`,
							value: this.MORE_ACTIONS_ITEM_VALUE.ACTIVATE,
						},
					] : []),
				]),
			];
		},
		runnitTokens () {
			if (!this.localBalanceAccount) return 0;
			return calcRunnitTokensDataForBalanceAccount(this.localBalanceAccount).runnitTokens;
		},
		isEnterprise (): boolean {
			return !!this.companyClubOfferBenefits.isEnterprise;
		},
	},
	async created () {
		await this.bindLocalBalanceAccount(this.currTeam);
	},
	watch: {
		currTeam: {
			immediate: false,
			async handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id;
				const oldTeamId: string = oldVal?.id;
				if (newTeamId !== oldTeamId) {
					await this.bindLocalBalanceAccount(newVal);
				}
			},
		},
	},
	methods: {
		onViewTeam () {
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.TEAM_DASHBOARD,
				params: {
					teamId: this.currTeam.id,
				},
				query: this.$route.query,
			});
		},
		onSelectedMoreActions (moreActionsItemValue: string) {
			switch (moreActionsItemValue) {
				case this.MORE_ACTIONS_ITEM_VALUE.EDIT:
					if (!this.hasManageCompanyPermission && !this.hasManageTeamsPermission) return;
					this.$emit('edit-teams', { teams: [this.currTeam] });
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.TRANSFER_TOKENS:
					if (!this.hasManageCompanyPermission) return;
					this.$emit('transfer-tokens', { team: this.currTeam });
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.DEACTIVATE:
					if (!this.hasManageTeamsPermission) return;
					this.$emit('deactivate-teams', { teams: [this.currTeam] });
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.ACTIVATE:
					if (!this.hasManageTeamsPermission) return;
					this.$emit('activate-teams', { teams: [this.currTeam] });
					break;
				case this.MORE_ACTIONS_ITEM_VALUE.DUP:
					if (!this.hasManageTeamsPermission) return;
					this.$emit('new-team', { duplicateTeam: this.currTeam });
					break;
				default:
					break;
			}
		},
		async bindLocalBalanceAccount (currTeam: Team) {
			if (!currTeam?.id) return;

			try {
				this.localBalanceAccount = null;
				this.loadingLocalBalanceAccount = true;
				await this.$bind(
					'localBalanceAccount',
					db
						.collection('balanceAccounts')
						.doc(currTeam.id),
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding balance account data:', error);
			} finally {
				this.loadingLocalBalanceAccount = false;
			}
		},
	},
	components: {
		ClipboardCopy,
		TeamMembersTableCell,
		BaseStyledMenu,
		RunnitTokens,
		IsBoundToBudgetBadge,
	},
});
